import React from "react";
import { motion } from "framer-motion";
import {
  Receipt,
  CreditCard,
  FileText,
  DollarSign,
  ClipboardCheck,
  Clock3,
  ShieldCheck,
  Check,
  ArrowRight,
  WalletCards,
  Building2,
  ChartNoAxesCombined,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";
import { useDispatch } from "react-redux";
import {
  handleLoginSignUpModal,
  LOGINMODAL,
} from "../../../Redux/actions/commonActions";

const Billing = () => {

  const dispatch = useDispatch();

  const handleLoginModal = () => {
    dispatch(
      handleLoginSignUpModal({
        data: true,
        actionType: LOGINMODAL,
      })
    );
  };
  const billingFeatures = [
    {
      icon: Receipt,
      title: "Invoices",
      description:
        "Create and manage patient invoices as part of your healthcare billing workflow.",
    },
    {
      icon: CreditCard,
      title: "Payment Management",
      description:
        "Keep track of payment information and billing activity across supported workflows.",
    },
    {
      icon: FileText,
      title: "Billing Records",
      description:
        "Maintain organised financial and billing information connected to patient care.",
    },
    {
      icon: ClipboardCheck,
      title: "Claims & Administration",
      description:
        "Support administrative billing and claims workflows where applicable.",
    },
  ];

  const steps = [
    {
      number: "01",
      icon: FileText,
      title: "Create the Bill",
      description:
        "Generate billing information based on the healthcare service provided.",
    },
    {
      number: "02",
      icon: Receipt,
      title: "Review Charges",
      description:
        "Review services, charges and relevant billing details before processing.",
    },
    {
      number: "03",
      icon: CreditCard,
      title: "Process Payment",
      description:
        "Record and manage payment activity through supported payment workflows.",
    },
    {
      number: "04",
      icon: ChartNoAxesCombined,
      title: "Track Billing",
      description:
        "Monitor billing activity and keep financial information organised.",
    },
  ];

  const benefits = [
    "Organise patient billing information",
    "Create and manage invoices",
    "Track supported payment activity",
    "Maintain billing records",
    "Support administrative workflows",
    "Connect billing with the broader healthcare workflow",
  ];

  const faqs = [
    {
      question: "What is healthcare billing?",
      answer:
        "Healthcare billing is the process of recording services, generating charges and managing payment-related information associated with patient care.",
    },
    {
      question: "Can Doxzon generate invoices?",
      answer:
        "Where billing functionality is supported, healthcare professionals can create and manage invoices as part of their billing workflow.",
    },
    {
      question: "Can I track payments?",
      answer:
        "Supported billing workflows can help healthcare professionals keep track of payment-related information and billing activity.",
    },
    {
      question: "Does billing connect with patient records?",
      answer:
        "Where the platform supports the relevant workflow, billing information can be connected with other healthcare and administrative processes.",
    },
    {
      question: "Does Doxzon support insurance claims?",
      answer:
        "Insurance and claims functionality depends on the specific Doxzon implementation and integrations available to the healthcare organisation.",
    },
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute inset-0 pointer-events-none">

            <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-teal-200/25 blur-3xl" />

            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-200/25 blur-3xl" />

            <div
              className="absolute inset-0 opacity-[0.025]"
              style={{
                backgroundImage:
                  "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />

          </div>

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-16 items-center">

            {/* LEFT */}
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Healthcare Billing
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Simplify your
                <span className="block text-teal-500 mt-2">
                  healthcare billing.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Manage invoices, payments and billing information through a
                connected workflow designed for healthcare professionals.
              </p>

              {/* BILLING SUMMARY */}
              <div className="mt-9 rounded-2xl bg-white border border-slate-100 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.10)]">

                <div className="flex items-center gap-4">

                  <div className="w-12 h-12 rounded-xl bg-teal-50 grid place-items-center">

                    <Receipt
                      size={22}
                      className="text-teal-600"
                    />

                  </div>

                  <div className="flex-1">

                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                      Billing Workspace
                    </p>

                    <p className="font-semibold mt-1">
                      Manage financial workflows
                    </p>

                  </div>
                  <motion.button
                    onClick={handleLoginModal}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-5 h-12 text-sm font-semibold"
                  >
                    Get Started
                    <ArrowRight size={15} />
                  </motion.button>

                </div>

                <button onClick={handleLoginModal} className="sm:hidden w-full mt-4 h-12 rounded-xl bg-slate-950 text-white font-semibold text-sm">
                  Get Started
                </button>

              </div>

              <div className="flex flex-wrap gap-6 mt-7 text-sm text-slate-500">

                <span className="inline-flex items-center gap-2">
                  <ShieldCheck size={16} className="text-teal-500" />
                  Secure workflows
                </span>

                <span className="inline-flex items-center gap-2">
                  <ClipboardCheck size={16} className="text-teal-500" />
                  Organised billing
                </span>

              </div>

            </motion.div>

            {/* RIGHT BILLING CARD */}
            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative bg-white rounded-[30px] border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-6 md:p-7">

                <div className="flex items-center justify-between pb-5 border-b border-slate-100">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Billing Overview
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Today's Billing
                    </h3>

                  </div>

                  <div className="w-11 h-11 rounded-xl bg-teal-50 grid place-items-center">

                    <WalletCards
                      size={21}
                      className="text-teal-600"
                    />

                  </div>

                </div>

                {/* SUMMARY */}
                <div className="grid grid-cols-2 gap-3 mt-6">

                  <div className="rounded-2xl bg-teal-50 p-4">

                    <p className="text-[10px] uppercase tracking-wider text-teal-600">
                      Invoices
                    </p>

                    <p className="font-sora text-2xl font-semibold mt-2">
                      24
                    </p>

                    <p className="text-[10px] text-slate-400 mt-1">
                      Billing records
                    </p>

                  </div>

                  <div className="rounded-2xl bg-slate-50 p-4">

                    <p className="text-[10px] uppercase tracking-wider text-slate-400">
                      Payments
                    </p>

                    <p className="font-sora text-2xl font-semibold mt-2">
                      18
                    </p>

                    <p className="text-[10px] text-slate-400 mt-1">
                      Processed
                    </p>

                  </div>

                </div>

                {/* BILLING ITEMS */}
                <div className="mt-5">

                  <div className="flex items-center justify-between mb-3">

                    <p className="text-sm font-semibold">
                      Recent billing
                    </p>

                    <Clock3
                      size={17}
                      className="text-teal-600"
                    />

                  </div>

                  <div className="space-y-2">

                    {[
                      ["Patient Invoice", "Completed"],
                      ["Consultation Bill", "Paid"],
                      ["Service Charge", "Pending"],
                    ].map(([title, status], index) => (

                      <div
                        key={title}
                        className="flex items-center gap-3 rounded-xl bg-slate-50 p-3"
                      >

                        <div className="w-9 h-9 rounded-lg bg-white grid place-items-center">

                          <Receipt
                            size={16}
                            className="text-teal-600"
                          />

                        </div>

                        <div className="flex-1">

                          <p className="text-xs font-semibold">
                            {title}
                          </p>

                          <p className="text-[10px] text-slate-400 mt-1">
                            Billing activity
                          </p>

                        </div>

                        <span
                          className={`text-[10px] font-semibold ${index === 2
                              ? "text-amber-500"
                              : "text-teal-600"
                            }`}
                        >
                          {status}
                        </span>

                      </div>

                    ))}

                  </div>

                </div>

                <button onClick={handleLoginModal} className="w-full mt-5 h-12 rounded-xl bg-slate-950 text-white font-semibold text-sm inline-flex items-center justify-center gap-2">

                  Open Billing

                  <ArrowRight size={16} />

                </button>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            BILLING FEATURES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Billing Features
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Bring billing into
                <span className="text-teal-500">
                  {" "}your workflow.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Keep financial and administrative healthcare workflows
                organised alongside the services you provide.
              </p>

            </div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">

  {billingFeatures.map((item, index) => {

    const Icon = item.icon;

    return (
      <motion.div
        key={item.title}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{
          duration: 0.5,
          delay: index * 0.06,
        }}
        whileHover={{ y: -4 }}
        className="group rounded-xl border border-slate-100 bg-slate-50/70 p-4 hover:bg-white hover:shadow-md hover:border-teal-100 transition"
      >

        <div className="w-9 h-9 rounded-lg bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">
          <Icon
            size={17}
            className="text-teal-600 group-hover:text-white transition"
          />
        </div>

        <h3 className="font-sora text-sm font-semibold mt-3">
          {item.title}
        </h3>

        <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
          {item.description}
        </p>

        <div className="inline-flex items-center gap-1.5 text-teal-600 text-[11px] font-semibold mt-3">
          Explore
          <ArrowRight size={12} />
        </div>

      </motion.div>
    );
  })}

</div>

          </div>

        </section>

        {/* =====================================================
            HOW IT WORKS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                How It Works
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Billing made
                <span className="text-teal-500">
                  {" "}simpler.
                </span>

              </h2>

            </div>

            <div className="relative grid md:grid-cols-4 gap-6 mt-14">

              <div className="hidden md:block absolute top-7 left-[11%] right-[11%] h-px bg-teal-100" />

              {steps.map((item) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.number}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="relative text-center"
                  >

                    <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-slate-950 border-4 border-[#f6fbfc] shadow-lg grid place-items-center">

                      <Icon
                        size={21}
                        className="text-teal-400"
                      />

                    </div>

                    <span className="block mt-5 text-[10px] font-bold tracking-[0.2em] text-teal-600">
                      STEP {item.number}
                    </span>

                    <h3 className="font-sora text-lg font-semibold mt-2">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-6 mt-2 max-w-[230px] mx-auto">
                      {item.description}
                    </p>

                  </motion.div>

                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            FINANCIAL WORKFLOW
        ====================================================== */}
        <section className="py-24 md:py-28 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Connected Billing
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Connect financial
                <span className="block text-teal-400">
                  workflows with care.
                </span>

              </h2>

              <p className="text-slate-400 text-lg leading-8 mt-6 max-w-xl">
                Billing works alongside the wider healthcare workflow, helping
                providers keep service, patient and financial information
                organised.
              </p>

            </div>

            <div className="space-y-3">

              {[
                [Building2, "Healthcare Services"],
                [FileText, "Patient Records"],
                [Receipt, "Invoices"],
                [CreditCard, "Payments"],
              ].map(([Icon, title], index) => (

                <motion.div
                  key={title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.07 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                >

                  <div className="w-10 h-10 rounded-xl bg-teal-500/10 grid place-items-center">

                    <Icon
                      size={18}
                      className="text-teal-400"
                    />

                  </div>

                  <span className="text-sm font-medium text-slate-300">
                    {title}
                  </span>

                  <ArrowRight
                    size={16}
                    className="ml-auto text-slate-500"
                  />

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            BENEFITS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-gradient-to-br from-cyan-50 to-blue-50">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Provider Benefits
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Spend less time
                <span className="block text-teal-500">
                  managing billing.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6 max-w-xl">
                Keep your billing processes structured so you can focus more
                time on delivering healthcare.
              </p>

            </div>

            <div className="space-y-3">

              {benefits.map((item, index) => (

                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white bg-white/80 p-4 shadow-sm"
                >

                  <span className="w-8 h-8 rounded-full bg-teal-50 text-teal-600 grid place-items-center shrink-0">

                    <Check size={15} />

                  </span>

                  <span className="text-sm md:text-base font-medium text-slate-700">
                    {item}
                  </span>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-4xl mx-auto px-6">

            <div className="text-center">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                FAQ
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Frequently asked questions
              </h2>

            </div>

            <div className="space-y-3 mt-12">

              {faqs.map((faq, index) => (

                <motion.details
                  key={faq.question}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group rounded-2xl border border-slate-100 bg-slate-50 open:bg-white open:shadow-sm"
                >

                  <summary className="cursor-pointer list-none flex items-center justify-between gap-5 p-6 font-sora font-semibold">

                    <span>
                      {faq.question}
                    </span>

                    <span className="w-8 h-8 rounded-full bg-white group-open:bg-teal-500 group-open:text-white grid place-items-center shrink-0 transition">

                      <ArrowRight
                        size={15}
                        className="group-open:rotate-90 transition-transform"
                      />

                    </span>

                  </summary>

                  <p className="px-6 pb-6 text-slate-600 leading-7 text-sm md:text-base">
                    {faq.answer}
                  </p>

                </motion.details>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FINAL CTA
        ====================================================== */}
        <section className="px-6 pb-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-slate-950 text-white px-8 py-16 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />

            <div className="relative">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Healthcare Professionals
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Make billing part of
                <span className="text-teal-400">
                  {" "}one connected workflow.
                </span>

              </h2>

              <p className="text-slate-300 max-w-2xl mx-auto mt-5 leading-7">
                Organise supported invoices, payments and billing information
                alongside your healthcare operations.
              </p>

              <button onClick={handleLoginModal} className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-7 py-4 font-semibold hover:bg-teal-50 transition">

                Explore Billing

                <ArrowRight size={17} />

              </button>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default Billing;